import { create } from "zustand";
import type { UnifiedEvent } from "@/lib/types";

export type ViewMode = "live" | "trace" | "sessions" | "explore";
export type DensityMode = "compact" | "comfortable";
export type SeverityFilter = "all" | "debug" | "info" | "warn" | "error";

export type TelemetryFilters = {
  search: string;
  service: string;
  sessionId: string;
  sessionEventId: string;
  traceId: string;
  severity: SeverityFilter;
  errorOnly: boolean;
};

type TelemetryStore = {
  view: ViewMode;
  density: DensityMode;
  paused: boolean;
  filters: TelemetryFilters;
  selectedTraceId: string | null;
  selectedSessionId: string | null;
  selectedEvent: UnifiedEvent | null;
  liveEvents: UnifiedEvent[];
  backlogEvents: UnifiedEvent[];
  setView: (view: ViewMode) => void;
  setDensity: (mode: DensityMode) => void;
  setPaused: (value: boolean) => void;
  setFilters: (next: Partial<TelemetryFilters>) => void;
  clearFilters: () => void;
  setSelectedTraceId: (traceId: string | null) => void;
  setSelectedSessionId: (sessionId: string | null) => void;
  setSelectedEvent: (event: UnifiedEvent | null) => void;
  addIncomingEvents: (events: UnifiedEvent[]) => void;
  clearLiveEvents: () => void;
  catchUp: () => void;
};

const DEFAULT_FILTERS: TelemetryFilters = {
  search: "",
  service: "",
  sessionId: "",
  sessionEventId: "",
  traceId: "",
  severity: "all",
  errorOnly: false,
};

const MAX_LIVE_EVENTS = 8_000;
const MAX_BACKLOG_EVENTS = 4_000;

const mergeByKey = (existing: UnifiedEvent[], incoming: UnifiedEvent[], max: number): UnifiedEvent[] => {
  const map = new Map<string, UnifiedEvent>();
  for (const event of existing) {
    map.set(event.key, event);
  }
  for (const event of incoming) {
    map.set(event.key, event);
  }
  const merged = [...map.values()].sort((a, b) => b.tsMs - a.tsMs || a.key.localeCompare(b.key));
  return merged.slice(0, max);
};

export const useTelemetryStore = create<TelemetryStore>((set, get) => ({
  view: "live",
  density: "compact",
  paused: false,
  filters: DEFAULT_FILTERS,
  selectedTraceId: null,
  selectedSessionId: null,
  selectedEvent: null,
  liveEvents: [],
  backlogEvents: [],
  setView: (view) => set({ view }),
  setDensity: (density) => set({ density }),
  setPaused: (paused) => set({ paused }),
  setFilters: (next) => set((state) => ({ filters: { ...state.filters, ...next } })),
  clearFilters: () => set({ filters: DEFAULT_FILTERS }),
  setSelectedTraceId: (selectedTraceId) => set({ selectedTraceId }),
  setSelectedSessionId: (selectedSessionId) => set({ selectedSessionId }),
  setSelectedEvent: (selectedEvent) => set({ selectedEvent }),
  addIncomingEvents: (events) => {
    if (events.length === 0) return;
    const { paused, liveEvents, backlogEvents } = get();
    if (paused) {
      set({ backlogEvents: mergeByKey(backlogEvents, events, MAX_BACKLOG_EVENTS) });
      return;
    }
    set({ liveEvents: mergeByKey(liveEvents, events, MAX_LIVE_EVENTS) });
  },
  clearLiveEvents: () => set({ liveEvents: [], backlogEvents: [] }),
  catchUp: () => {
    const { liveEvents, backlogEvents } = get();
    if (backlogEvents.length === 0) return;
    set({ liveEvents: mergeByKey(liveEvents, backlogEvents, MAX_LIVE_EVENTS), backlogEvents: [] });
  },
}));
